// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { formatHumanName } from '@medplum/core';
import type { BundleEntry, Observation, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  INSURANCE_ELIGIBILITY_INELIGIBLE_TAG,
  isTestPatient,
  POLARIS_SYSTEM_STATUS_TAG_SYSTEM,
  TRIAGE_OBS_CATEGORY,
  TRIAGE_OBS_CATEGORY_SYSTEM,
  TRIAGE_OBS_CODES,
  type TriageIntakeSortColumnKey,
  type TriageSortState,
} from '@polarissleep/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { STEDI_PAYER_NETWORK_SYSTEM } from '../constants/eligibility';

const PERFORMER_EXTENSION_URL = 'http://polarissleep.com/extensions/performer';
const HSAT_ELIGIBILITY_MODULE_URI = 'http://polarissleep.com/guidance/hsat-eligibility';

const DENIED_TAG = `${INSURANCE_ELIGIBILITY_INELIGIBLE_TAG.system}|${INSURANCE_ELIGIBILITY_INELIGIBLE_TAG.code}`;
const TEST_PATIENT_TAG = 'http://polarissleep.com/fhir/tags|test-patient';

/**
 * GraphQL filter value for the `Observation.category` token search.
 * Shape: `<category-system>|patient-latest-triage`.
 */
const PATIENT_LATEST_CATEGORY_FILTER = `${TRIAGE_OBS_CATEGORY_SYSTEM}|${TRIAGE_OBS_CATEGORY.patientLatest}`;

/**
 * GraphQL filter value for the `Observation.category` token search — nullish marker series.
 * Shape: `<category-system>|patient-latest-triage-nullish`.
 */
const PATIENT_LATEST_NULLISH_CATEGORY_FILTER = `${TRIAGE_OBS_CATEGORY_SYSTEM}|${TRIAGE_OBS_CATEGORY.patientLatestNullish}`;

export interface IneligibleEntry {
  patientId: string | undefined;
  patientName: string;
  systemStatusCode: string | undefined;
  reason: string | undefined;
  errorCode: string | undefined;
  requestedStc: string | undefined;
  compositionId: string | undefined;
  intakeCompletedDate: string | undefined;
  evaluatedById: string | undefined;
  evaluatedBy: string | undefined;
  evaluatedOn: string | undefined;
  payorId: string | undefined;
  isTestPatient: boolean;
}

export interface UseIneligibleQueueReturn {
  entries: IneligibleEntry[];
  loading: boolean;
  total: number;
}

interface GRResult {
  occurrenceDateTime?: string;
  moduleUri?: string;
  reasonCode?: { coding?: { display?: string }[] }[];
  extension?: {
    url?: string;
    valueReference?: { resource?: { id?: string; name?: { given?: string[]; family?: string }[] } };
  }[];
}

interface PatientResult {
  id?: string;
  name?: { given?: string[]; family?: string }[];
  meta?: { tag?: { system?: string; code?: string }[] };
  CompositionList?: { id?: string; date?: string }[];
  GuidanceResponseList?: GRResult[];
  CoverageEligibilityResponseList?: {
    created?: string;
    insurer?: {
      resource?: {
        identifier?: { system?: string; value?: string }[];
      };
    };
    error?: { code?: { coding?: { display?: string }[] } }[];
    request?: {
      resource?: {
        purpose?: string[];
      };
    };
  }[];
}

interface ObservationWithPatientGraphQLResult {
  id?: string;
  valueQuantity?: { value?: number; unit?: string; code?: string };
  valueCodeableConcept?: { coding?: { system?: string; code?: string; display?: string }[] };
  subject?: {
    reference?: string;
    resource?: PatientResult;
  };
}

/**
 * Map an observation's subject (ineligible Patient) to an `IneligibleEntry`.
 * For the Observation sort branch, Patient is fetched via `subject.resource` in the GraphQL
 * query. For the null-bucket GraphQL branch, Patient is fetched via a PatientList query.
 */
function mapPatientToEntry(patient: PatientResult, patientId: string | undefined): IneligibleEntry {
  const patientName = patient.name?.[0] ? formatHumanName(patient.name[0]) : 'Unknown Patient';
  const composition = patient.CompositionList?.[0];
  const gr = patient.GuidanceResponseList?.find((g) => g.moduleUri === HSAT_ELIGIBILITY_MODULE_URI);
  const performerExt = gr?.extension?.find((e) => e.url === PERFORMER_EXTENSION_URL);
  const practitioner = performerExt?.valueReference?.resource;
  const evaluatedBy = practitioner?.name?.[0] ? formatHumanName(practitioner.name[0]) : undefined;
  const systemTag = patient.meta?.tag?.find((t) => t.system === POLARIS_SYSTEM_STATUS_TAG_SYSTEM);
  const cer = patient.CoverageEligibilityResponseList?.[0];
  const payorId = cer?.insurer?.resource?.identifier?.find((id) => id.system === STEDI_PAYER_NETWORK_SYSTEM)?.value;
  const errorCode = cer?.error?.[0]?.code?.coding?.[0]?.display;
  const requestedStc = cer?.request?.resource?.purpose?.join(', ');
  return {
    patientId: patientId ?? patient.id,
    patientName,
    systemStatusCode: systemTag?.code,
    reason: gr?.reasonCode?.[0]?.coding?.[0]?.display,
    errorCode,
    requestedStc,
    compositionId: composition?.id,
    intakeCompletedDate: cer?.created,
    evaluatedById: practitioner?.id,
    evaluatedBy,
    evaluatedOn: gr?.occurrenceDateTime,
    payorId,
    isTestPatient: isTestPatient(patient as Patient | undefined),
  };
}

/**
 * Build the `Observation._sort` value for a given sort column key + direction.
 * Mirrors `buildSortParam` in `useTriageQueue.ts`.
 * Only called for non-'name' columns that exist in TRIAGE_OBS_CODES.
 */
function buildObsSortParam(sort: TriageSortState): string {
  if (sort.column === 'name' || !(sort.column in TRIAGE_OBS_CODES)) {
    return '-_lastUpdated';
  }
  const spec = TRIAGE_OBS_CODES[sort.column as TriageIntakeSortColumnKey];
  const key = spec.valueField === 'valueQuantity' ? 'value-quantity' : 'value-concept';
  return sort.direction === 'asc' ? key : `-${key}`;
}

/**
 * The Patient sub-fields fetched in both the default PatientList branch and the
 * Observation sort branch (subject.resource projection).
 */
const PATIENT_ENTRY_FIELDS = `
  id
  name { given family }
  meta { tag { system code } }
  CompositionList(_reference: subject status: "final" _count: 1 _sort: "-_lastUpdated") {
    id
    date
  }
  GuidanceResponseList(_reference: subject _count: 1 _sort: "-_lastUpdated") {
    occurrenceDateTime
    moduleUri
    reasonCode { coding { display } }
    extension {
      url
      valueReference {
        resource {
          ... on Practitioner { id name { given family } }
        }
      }
    }
  }
  CoverageEligibilityResponseList(_reference: patient _count: 1 _sort: "-_lastUpdated") {
    created
    insurer {
      resource {
        ... on Organization {
          identifier { system value }
        }
      }
    }
    error { code { coding { display } } }
    request {
      resource {
        ... on CoverageEligibilityRequest {
          purpose
        }
      }
    }
  }
`;

export function useIneligibleQueue({
  page,
  pageSize,
  sort,
  search,
  hideTestPatients,
}: {
  page: number;
  pageSize: number;
  sort?: TriageSortState;
  search?: string;
  hideTestPatients?: boolean;
}): UseIneligibleQueueReturn {
  const medplum = useMedplum();
  const [entries, setEntries] = useState<IneligibleEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  // Stable primitive key for sort — prevents infinite fetch loop caused by sort
  // object identity changing on each render (mirrors useTriageQueue pattern).
  const sortKey = useMemo(() => (sort ? `${sort.column}|${sort.direction}` : null), [sort]);

  // Observation sort branch activates when sort is set AND column is not 'name'
  // AND search is NOT set (the search+sort branch handles that case).
  const useObservationSort = sort !== undefined && sort.column !== 'name' && sort.column in TRIAGE_OBS_CODES && !search;

  // NEW (amended Phase 10): filter+sort coexistence branch — both `sort` AND
  // `search` set on a non-`name` column. GraphQL does NOT expose chained-search
  // args (Appendix B Probe D), so we route through REST. Name column + search
  // keeps the V1 PatientList branch (handled in default branch below).
  const useSortAndSearchBranch =
    sort !== undefined && sort.column !== 'name' && !!search && sort.column in TRIAGE_OBS_CODES;

  // Shared hydration query builder used by both the sort+search branch and
  // the sort-only null-bucket branch.
  const patientCsvForGraphQL = useCallback(
    (csv: string): string => `
      query IneligibleSortHydration {
        PatientList(_id: "${csv}" _count: ${pageSize}) {
          ${PATIENT_ENTRY_FIELDS}
        }
      }
    `,
    [pageSize]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: sort tracked via sortKey (stable primitive) — object identity excluded intentionally. useObservationSort and useSortAndSearchBranch derived from sort + search.
  const fetchEntries = useCallback(async () => {
    setLoading(true);
    const offset = (page - 1) * pageSize;

    try {
      if (useSortAndSearchBranch && sort && search) {
        // ── Filter + sort coexistence branch (REST chained search) ──────────
        // Both `sort` AND `search` set. GraphQL does NOT expose chained-search
        // args (`subject:Patient.name`), so we route through REST. Denied-tag
        // scoping is applied via `subject:Patient._tag=<DENIED_TAG>` chained
        // predicate on the Observation query (validated Probe D3c shape).
        const spec = TRIAGE_OBS_CODES[sort.column as TriageIntakeSortColumnKey];
        const sortParam = buildObsSortParam(sort);
        const codeFilter = `${spec.system}|${spec.code}`;

        // 1) REST Patient count: `_tag=denied + name=<search> + _summary=count`.
        const countParams = new URLSearchParams();
        countParams.set('_tag', DENIED_TAG);
        countParams.set('name', search);
        countParams.set('_summary', 'count');
        const countBundle = await medplum.search('Patient', countParams);
        const totalMatchingIneligiblePatients = countBundle.total ?? 0;

        if (totalMatchingIneligiblePatients === 0) {
          // No ineligible patients match the search — short-circuit.
          setTotal(0);
          setEntries([]);
          return;
        }

        // 2) REST Observation search for the with-value bucket.
        // `subject:Patient._tag=<DENIED_TAG>` scopes to ineligible patients.
        // `subject:Patient.name=<search>` scopes to name-matching patients.
        const obsParams = new URLSearchParams();
        obsParams.set('category', PATIENT_LATEST_CATEGORY_FILTER);
        obsParams.set('code', codeFilter);
        obsParams.set('subject:Patient._tag', DENIED_TAG);
        obsParams.set('subject:Patient.name', search);
        obsParams.set('_sort', sortParam);
        obsParams.set('_count', String(pageSize));
        obsParams.set('_offset', String(offset));
        obsParams.set('_total', 'accurate');
        const obsBundle = await medplum.search('Observation', obsParams);
        const rawWithValueCount = obsBundle.total ?? 0;
        const obsEntries = obsBundle.entry ?? [];
        const withValuePatientIds: string[] = [];
        for (const entry of obsEntries) {
          const obs = entry.resource as Observation | undefined;
          const subjectRef = obs?.subject?.reference;
          if (!subjectRef?.startsWith('Patient/')) {
            continue;
          }
          withValuePatientIds.push(subjectRef.slice('Patient/'.length));
        }

        // Clamp withValueCount against totalMatchingIneligiblePatients to handle
        // atomic-swap races (mirrors useTriageQueue and existing Observation-sort
        // branch behavior).
        const withValueCount = Math.min(rawWithValueCount, totalMatchingIneligiblePatients);
        const nullBucketCount = Math.max(0, totalMatchingIneligiblePatients - withValueCount);

        // 3) Hydrate the with-value page slice via GraphQL PatientList(_id: <csv>)
        // carrying the same PATIENT_ENTRY_FIELDS projection as the default branch.
        const withValuePatientsById = new Map<string, PatientResult>();
        if (withValuePatientIds.length > 0) {
          const hydrationResult = await medplum.graphql(patientCsvForGraphQL(withValuePatientIds.join(',')));
          const patientList = (hydrationResult.data?.PatientList ?? []) as PatientResult[];
          for (const p of patientList) {
            if (p.id) {
              withValuePatientsById.set(p.id, p);
            }
          }
        }

        // Build with-value rows preserving REST sort order.
        const withValueRows: IneligibleEntry[] = withValuePatientIds
          .map((patientId) => {
            const patient = withValuePatientsById.get(patientId);
            if (!patient) {
              return null;
            }
            return mapPatientToEntry(patient, patientId);
          })
          .filter(Boolean) as IneligibleEntry[];

        // 4) Null-bucket (only when straddling/beyond boundary).
        const pageStart = offset;
        const pageEnd = offset + pageSize;
        const straddlesBoundary = pageStart < withValueCount && pageEnd > withValueCount;
        const beyondBoundary = pageStart >= withValueCount;
        const needsNullBucket = nullBucketCount > 0 && (straddlesBoundary || beyondBoundary);

        let nullRows: IneligibleEntry[] = [];
        if (needsNullBucket) {
          let nullOffset: number;
          let nullCount: number;
          if (sort.direction === 'desc') {
            nullOffset = Math.max(0, pageStart - withValueCount);
            const withValueOnPage = withValueRows.length;
            nullCount = Math.max(0, Math.min(pageSize - withValueOnPage, nullBucketCount - nullOffset));
          } else {
            nullOffset = pageStart;
            if (pageStart >= nullBucketCount) {
              nullCount = 0;
            } else {
              nullCount = Math.min(pageSize, nullBucketCount - pageStart);
            }
          }

          if (nullCount > 0) {
            // Positive-marker null-bucket query:
            // Observation?category=patient-latest-triage-nullish
            //   &code=<col>
            //   &subject:Patient._tag=<DENIED_TAG>
            //   &subject:Patient.name=<search>
            //   &_sort=-_lastUpdated&_count=<nullCount>&_offset=<nullOffset>
            // No exclusion set needed — markers only exist for patients WITHOUT a real value.
            const nullMarkerParams = new URLSearchParams();
            nullMarkerParams.set('category', PATIENT_LATEST_NULLISH_CATEGORY_FILTER);
            nullMarkerParams.set('code', codeFilter);
            nullMarkerParams.set('subject:Patient._tag', DENIED_TAG);
            nullMarkerParams.set('subject:Patient.name', search);
            nullMarkerParams.set('_sort', '-_lastUpdated');
            nullMarkerParams.set('_count', String(nullCount));
            nullMarkerParams.set('_offset', String(nullOffset));

            const nullMarkerBundle = await medplum.search('Observation', nullMarkerParams);
            const nullMarkerEntries = nullMarkerBundle.entry ?? [];
            const nullPatientIds: string[] = [];
            for (const entry of nullMarkerEntries) {
              const obs = entry.resource as Observation | undefined;
              const subjectRef = obs?.subject?.reference;
              if (subjectRef?.startsWith('Patient/')) {
                nullPatientIds.push(subjectRef.slice('Patient/'.length));
              }
            }

            // Hydrate null-bucket Patients via GraphQL PatientList for full
            // sub-resource projection parity (CompositionList + GuidanceResponseList).
            if (nullPatientIds.length > 0) {
              const nullHydrationResult = await medplum.graphql(patientCsvForGraphQL(nullPatientIds.join(',')));
              const nullPatientList = (nullHydrationResult.data?.PatientList ?? []) as PatientResult[];
              const nullPatientsById = new Map<string, PatientResult>();
              for (const p of nullPatientList) {
                if (p.id) {
                  nullPatientsById.set(p.id, p);
                }
              }
              nullRows = nullPatientIds
                .map((patientId) => {
                  const patient = nullPatientsById.get(patientId);
                  if (!patient) {
                    return null;
                  }
                  return mapPatientToEntry(patient, patientId);
                })
                .filter(Boolean) as IneligibleEntry[];
            }
          }
        }

        // Merge per direction (plan §2 null-bucket order):
        //   asc → null bucket first, then with-value asc.
        //   desc → with-value first, then null bucket.
        const merged = sort.direction === 'asc' ? [...nullRows, ...withValueRows] : [...withValueRows, ...nullRows];

        setTotal(totalMatchingIneligiblePatients);
        setEntries(hideTestPatients ? merged.filter((e) => !e.isTestPatient) : merged);
        return;
      }

      if (useObservationSort && sort) {
        // ── Observation sort branch ───────────────────────────────────────────
        // Scope to ineligible patients via two-step:
        //   (1) PatientConnection + Patient IDs from PatientList(_tag=<denied-tag>)
        //   (2) ObservationList(...&subject=<csv-ids>)

        const spec = TRIAGE_OBS_CODES[sort.column as TriageIntakeSortColumnKey];
        const sortParam = buildObsSortParam(sort);
        const codeFilter = `${spec.system}|${spec.code}`;

        // FALLBACK: Fetch all denied patient IDs first, then scope ObservationList.
        // Two-step fetch: count + IDs. We need the IDs for the subject filter
        // and the count for null-bucket math. Fetch a large page to get all IDs
        // (ineligible list is typically small — O(100s) not O(10k+)).
        const patientCountQuery = `
          query IneligibleCount {
            PatientConnection(_tag: "${DENIED_TAG}") { count }
          }
        `;
        const countResult = await medplum.graphql(patientCountQuery);
        const totalIneligibleCount = (countResult.data?.PatientConnection as { count: number } | undefined)?.count ?? 0;

        if (totalIneligibleCount === 0) {
          // No ineligible patients — short-circuit.
          setTotal(0);
          setEntries([]);
          return;
        }

        // Fetch up to totalIneligibleCount IDs in a single query.
        const patientIdsQuery = `
          query IneligiblePatientIds {
            PatientList(_tag: "${DENIED_TAG}" _count: ${totalIneligibleCount}) {
              id
            }
          }
        `;
        const idsResult = await medplum.graphql(patientIdsQuery);
        const patientList = (idsResult.data?.PatientList ?? []) as { id?: string }[];
        const deniedPatientIds = patientList.map((p) => p.id).filter((id): id is string => Boolean(id));

        if (deniedPatientIds.length === 0) {
          // Short-circuit; degenerate (count was non-zero but IDs returned nothing).
          setTotal(0);
          setEntries([]);
          return;
        }

        const hasOrSubjectFilter = `subject: "${deniedPatientIds.map((id) => `Patient/${id}`).join(',')}"`;

        const observationQuery = `
          query IneligibleQueueSorted {
            ObservationList(
              category: "${PATIENT_LATEST_CATEGORY_FILTER}"
              code: "${codeFilter}"
              _sort: "${sortParam}"
              _count: ${pageSize}
              _offset: ${offset}
              ${hasOrSubjectFilter}
            ) {
              id
              valueQuantity { value unit code }
              valueCodeableConcept { coding { system code display } }
              subject {
                reference
                resource {
                  ... on Patient {
                    ${PATIENT_ENTRY_FIELDS}
                  }
                }
              }
            }
            ObservationConnection(
              category: "${PATIENT_LATEST_CATEGORY_FILTER}"
              code: "${codeFilter}"
              ${hasOrSubjectFilter}
            ) { count }
          }
        `;

        const obsResult = await medplum.graphql(observationQuery);
        const observationList = (obsResult.data?.ObservationList ?? []) as ObservationWithPatientGraphQLResult[];
        const withValueCount = (obsResult.data?.ObservationConnection as { count: number } | undefined)?.count ?? 0;

        // Clamp withValueCount against totalIneligibleCount to handle
        // atomic-swap races where a new Observation is written between the count
        // fetch and the Observation query.
        const rawWithValueCount = withValueCount;
        const clampedWithValueCount = Math.min(rawWithValueCount, totalIneligibleCount);
        if (rawWithValueCount > totalIneligibleCount) {
          console.warn(
            `[useIneligibleQueue] withValueCount (${rawWithValueCount}) exceeds totalIneligibleCount (${totalIneligibleCount}). ` +
              'Possible atomic-swap race — clamping to prevent negative null-bucket count.'
          );
        }
        const nullBucketCount = Math.max(0, totalIneligibleCount - clampedWithValueCount);

        // ── Map with-value rows ───────────────────────────────────────────────
        const withValueRows: IneligibleEntry[] = observationList
          .map((obs) => {
            const patient = obs.subject?.resource;
            if (!patient) {
              return null;
            }
            const patientId = obs.subject?.reference?.startsWith('Patient/')
              ? obs.subject.reference.slice('Patient/'.length)
              : patient.id;
            return mapPatientToEntry(patient, patientId);
          })
          .filter(Boolean) as IneligibleEntry[];

        // ── Decide whether to fetch null bucket ───────────────────────────────
        const pageStart = offset;
        const pageEnd = offset + pageSize;
        const straddlesBoundary = pageStart < clampedWithValueCount && pageEnd > clampedWithValueCount;
        const beyondBoundary = pageStart >= clampedWithValueCount;
        const needsNullBucket = nullBucketCount > 0 && (straddlesBoundary || beyondBoundary);

        let nullRows: IneligibleEntry[] = [];
        if (needsNullBucket) {
          let nullOffset: number;
          let nullCount: number;
          if (sort.direction === 'desc') {
            nullOffset = Math.max(0, pageStart - clampedWithValueCount);
            const withValueOnPage = withValueRows.length;
            nullCount = Math.max(0, Math.min(pageSize - withValueOnPage, nullBucketCount - nullOffset));
          } else {
            nullOffset = pageStart;
            if (pageStart >= nullBucketCount) {
              nullCount = 0;
            } else {
              nullCount = Math.min(pageSize, nullBucketCount - pageStart);
            }
          }

          if (nullCount > 0) {
            // ── Positive-marker null-bucket query ────────────────────────────
            // Replaces the exclusion-set pre-fetch + PatientList null-bucket query.
            // Queries Observation?category=patient-latest-triage-nullish&code=<col>
            //   &subject:Patient._tag=<DENIED_TAG>
            //   &_sort=-_lastUpdated&_count=<nullCount>&_offset=<nullOffset>
            // No client-side filtering or pre-fetch round trip required.
            const nullMarkerParams = new URLSearchParams();
            nullMarkerParams.set('category', PATIENT_LATEST_NULLISH_CATEGORY_FILTER);
            nullMarkerParams.set('code', codeFilter);
            nullMarkerParams.set('subject:Patient._tag', DENIED_TAG);
            nullMarkerParams.set('_sort', '-_lastUpdated');
            nullMarkerParams.set('_count', String(nullCount));
            nullMarkerParams.set('_offset', String(nullOffset));

            const nullMarkerBundle = await medplum.search('Observation', nullMarkerParams);
            const nullMarkerEntries = nullMarkerBundle.entry ?? [];
            const nullPatientIds: string[] = [];
            for (const entry of nullMarkerEntries) {
              const obs = entry.resource as Observation | undefined;
              const subjectRef = obs?.subject?.reference;
              if (subjectRef?.startsWith('Patient/')) {
                nullPatientIds.push(subjectRef.slice('Patient/'.length));
              }
            }

            // Hydrate null-bucket Patients via GraphQL PatientList for full
            // sub-resource projection parity (CompositionList + GuidanceResponseList).
            if (nullPatientIds.length > 0) {
              const nullHydrationResult = await medplum.graphql(patientCsvForGraphQL(nullPatientIds.join(',')));
              const nullPatientList = (nullHydrationResult.data?.PatientList ?? []) as PatientResult[];
              const nullPatientsById = new Map<string, PatientResult>();
              for (const p of nullPatientList) {
                if (p.id) {
                  nullPatientsById.set(p.id, p);
                }
              }
              nullRows = nullPatientIds
                .map((patientId) => {
                  const patient = nullPatientsById.get(patientId);
                  if (!patient) {
                    return null;
                  }
                  return mapPatientToEntry(patient, patientId);
                })
                .filter(Boolean) as IneligibleEntry[];
            }
          }
        }

        // ── Merge per direction ───────────────────────────────────────────────
        // desc: with-value desc first, then null bucket last.
        // asc: null bucket first, then with-value asc.
        const merged = sort.direction === 'asc' ? [...nullRows, ...withValueRows] : [...withValueRows, ...nullRows];

        setTotal(totalIneligibleCount);
        setEntries(hideTestPatients ? merged.filter((e) => !e.isTestPatient) : merged);
        return;
      }

      // ── Default branch (no sort, OR sort.column === 'name', OR search-only) ─
      // PatientList rooted on the denied-tag. For 'name' sort, use _sort=family.
      // For `search` (with or without name-sort), add the `name` predicate so
      // search input on TriagePage still scopes results when the Ineligible tab
      // is active. Byte-equivalent to V1 when both sort and search are absent.
      let sortParam: string;
      if (!sort || sort.column !== 'name') {
        sortParam = '-_lastUpdated';
      } else {
        sortParam = sort.direction === 'desc' ? '-given' : 'given';
      }

      const searchPredicate = search ? ` name: "${search}"` : '';

      if (hideTestPatients && !useObservationSort && !useSortAndSearchBranch) {
        const patientParams = new URLSearchParams();
        patientParams.set('_tag', DENIED_TAG);
        patientParams.set('_tag:not', TEST_PATIENT_TAG);
        if (search) {
          patientParams.set('name', search);
        }
        patientParams.set('_sort', sortParam);
        patientParams.set('_count', String(pageSize));
        patientParams.set('_offset', String(offset));
        patientParams.set('_total', 'accurate');

        const patientBundle = await medplum.search('Patient', patientParams);
        const patientIds = (patientBundle.entry ?? [])
          .map((entry: BundleEntry) => (entry.resource as Patient | undefined)?.id)
          .filter((id: string | undefined): id is string => Boolean(id));
        const hydration =
          patientIds.length > 0
            ? await medplum.graphql(patientCsvForGraphQL(patientIds.join(',')))
            : { data: { PatientList: [] } };
        const mapped = ((hydration.data?.PatientList ?? []) as PatientResult[]).map((patient) =>
          mapPatientToEntry(patient, patient.id)
        );
        setTotal(patientBundle.total ?? 0);
        setEntries(mapped);
        return;
      }

      const query = `
        query IneligibleQueue {
          PatientList(_tag: "${DENIED_TAG}"${searchPredicate} _count: ${pageSize} _offset: ${offset} _sort: "${sortParam}") {
            ${PATIENT_ENTRY_FIELDS}
          }
          PatientConnection(_tag: "${DENIED_TAG}"${searchPredicate}) { count }
        }
      `;

      const result = await medplum.graphql(query);
      const list = (result.data?.PatientList ?? []) as PatientResult[];
      const connection = result.data?.PatientConnection as { count: number } | undefined;
      setTotal(connection?.count ?? 0);

      let mapped = list.map((patient) => mapPatientToEntry(patient, patient.id));

      // Client-side sort for fields not sortable via FHIR search params.
      if (sort?.column === 'intakeDate') {
        mapped = mapped.sort((a, b) => {
          const aVal = a.intakeCompletedDate ?? '';
          const bVal = b.intakeCompletedDate ?? '';
          return sort.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        });
      } else if (sort?.column === 'systemStatus') {
        mapped = mapped.sort((a, b) => {
          const aVal = a.systemStatusCode ?? '';
          const bVal = b.systemStatusCode ?? '';
          return sort.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        });
      }

      setEntries(hideTestPatients ? mapped.filter((e) => !e.isTestPatient) : mapped);
    } finally {
      setLoading(false);
    }
  }, [
    medplum,
    page,
    pageSize,
    sortKey,
    useObservationSort,
    useSortAndSearchBranch,
    search,
    patientCsvForGraphQL,
    hideTestPatients,
  ]);

  useEffect(() => {
    fetchEntries().catch(console.error);
  }, [fetchEntries]);

  return { entries, loading, total };
}
